import { useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, Sparkles, Check, ArrowRight, Phone, Clock, Crown, Wand2 } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import ConciergeChat from "@/components/site/ConciergeChat";

const HERO_IMG =
    "https://images.unsplash.com/photo-1604654894610-df63bc536371?auto=format&fit=crop&q=80&w=2400";

export default function CityLanding({ city }) {
    // SEO — set page title and description on mount
    useEffect(() => {
        const prevTitle = document.title;
        document.title = `Mobile Nails, Spray Tans & Facials in ${city.name} | 24K Glow On The Go`;
        const meta = document.querySelector('meta[name="description"]') || (() => {
            const m = document.createElement("meta");
            m.name = "description";
            document.head.appendChild(m);
            return m;
        })();
        const prevDesc = meta.content;
        meta.content = `${city.name}'s luxury mobile beauty salon. Monthly subscription for unlimited gel or acrylic sets, patent-pending AI 3D nail art, spray tans, and facials — delivered to your door across ${city.zipHint}.`;
        return () => {
            document.title = prevTitle;
            meta.content = prevDesc;
        };
    }, [city]);

    return (
        <div data-testid={`city-page-${city.slug}`} className="bg-ink text-white relative overflow-hidden bg-grain min-h-screen">
            <Navbar />

            {/* HERO */}
            <section className="relative min-h-[85vh] flex items-center pt-40 pb-16">
                <div className="absolute inset-0 z-0">
                    <img src={HERO_IMG} alt={`${city.name} mobile nail salon`} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-b from-ink/75 via-ink/60 to-ink" />
                    <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/30 to-transparent" />
                </div>

                <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 w-full">
                    <p className="text-xs tracking-[0.4em] uppercase text-gold mb-6 flex items-center gap-2">
                        <MapPin className="w-4 h-4" /> Serving {city.name}, FL · Same-day before 6pm
                    </p>
                    <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-5xl">
                        Luxury mobile nails
                        <br />
                        <span className="italic text-rose">& beauty services</span>
                        <br />
                        <span className="gold-underline">in {city.name}.</span>
                    </h1>
                    <p className="mt-6 max-w-2xl text-lg text-white/80 leading-relaxed italic font-serif">
                        The <span className="text-gold not-italic font-medium">24K glow</span> — on the go, brought to {city.landmarkPhrase}.
                    </p>
                    <p className="mt-8 max-w-2xl text-lg text-white/75 leading-relaxed">
                        {city.introCopy}
                    </p>

                    <div className="mt-10 flex flex-wrap gap-3">
                        <Link
                            to="/login?intent=subscribe"
                            data-testid={`city-cta-subscribe-${city.slug}`}
                            className="px-6 py-3 rounded-full bg-rose text-ink font-medium hover:bg-roseHover transition inline-flex items-center gap-2"
                        >
                            Book in {city.shortName} <ArrowRight className="w-4 h-4" />
                        </Link>
                        <a
                            href="tel:+19046729966"
                            data-testid={`city-cta-call-${city.slug}`}
                            className="px-6 py-3 rounded-full border border-rose text-rose hover:bg-rose hover:text-ink transition inline-flex items-center gap-2"
                        >
                            <Phone className="w-4 h-4" /> (904) 672-9966
                        </a>
                    </div>
                </div>
            </section>

            {/* ZIP / SERVICE AREA */}
            <section className="relative py-20 border-t border-hairline">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
                    <p className="text-xs tracking-[0.3em] uppercase text-rose">Service area</p>
                    <h2 className="font-serif text-3xl sm:text-4xl mt-3 max-w-3xl">
                        {city.name} neighborhoods we come to
                    </h2>
                    <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
                        {city.neighborhoods.map((n) => (
                            <div
                                key={n}
                                data-testid={`city-zip-${city.slug}-${n.replace(/\s+/g, "-").toLowerCase()}`}
                                className="rounded-2xl border border-white/10 bg-surface/60 p-4 text-center hover:border-rose/40 hover:bg-rose/5 transition"
                            >
                                <MapPin className="w-4 h-4 text-rose mx-auto mb-2" />
                                <p className="text-sm text-white/85">{n}</p>
                            </div>
                        ))}
                    </div>
                    <p className="mt-8 text-sm text-white/50 max-w-2xl">
                        Outside {city.name}? We also cover {city.nearby.join(", ")}. Message the concierge to confirm your zip.
                    </p>
                </div>
            </section>

            {/* PLANS */}
            <section className="relative py-24 border-t border-hairline bg-ink/60">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
                    <p className="text-xs tracking-[0.3em] uppercase text-rose">Two monthly plans</p>
                    <h2 className="font-serif text-4xl sm:text-5xl mt-3">Unlimited sets. Unlimited visits.</h2>
                    <div className="mt-12 grid md:grid-cols-2 gap-6">
                        <div className="rounded-3xl border border-white/10 bg-surface p-8">
                            <div className="flex items-center gap-2 text-rose">
                                <Wand2 className="w-4 h-4" />
                                <p className="text-xs tracking-[0.3em] uppercase">Glow</p>
                            </div>
                            <p className="font-serif text-5xl mt-3">$55<span className="text-lg text-white/50">/mo</span></p>
                            <p className="text-white/60 mt-2">Unlimited patent-pending gel sets in {city.shortName}.</p>
                            <ul className="mt-6 space-y-2 text-sm text-white/80">
                                {["Unlimited gel appointments", "7–14 day wear", "AI 3D nail art each visit", "Door-to-door service"].map((t) => (
                                    <li key={t} className="flex items-start gap-2"><Check className="w-4 h-4 text-rose mt-0.5" /> {t}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="rounded-3xl border border-gold/40 bg-gradient-to-br from-gold/8 via-transparent to-transparent p-8 relative">
                            <div className="absolute -top-3 right-6 px-3 py-1 rounded-full bg-gold text-ink text-[10px] tracking-[0.25em] uppercase">Best value</div>
                            <div className="flex items-center gap-2 text-gold">
                                <Crown className="w-4 h-4" />
                                <p className="text-xs tracking-[0.3em] uppercase">Acrylic</p>
                            </div>
                            <p className="font-serif text-5xl mt-3">$75<span className="text-lg text-white/50">/mo</span></p>
                            <p className="text-white/60 mt-2">Full acrylic set + unlimited fill-ins in {city.shortName}.</p>
                            <ul className="mt-6 space-y-2 text-sm text-white/80">
                                {["Monthly full acrylic set", "Unlimited fill-in visits", "AI 3D nail art each visit", "Priority booking"].map((t) => (
                                    <li key={t} className="flex items-start gap-2"><Check className="w-4 h-4 text-gold mt-0.5" /> {t}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <div className="mt-8">
                        <Link
                            to="/login?intent=subscribe"
                            data-testid={`city-plans-cta-${city.slug}`}
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-rose text-ink font-medium hover:bg-roseHover transition"
                        >
                            Subscribe & book <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* WHY LOCAL */}
            <section className="relative py-24 border-t border-hairline">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 grid md:grid-cols-2 gap-16 items-start">
                    <div>
                        <p className="text-xs tracking-[0.3em] uppercase text-rose">Why {city.shortName} loves us</p>
                        <h2 className="font-serif text-4xl sm:text-5xl mt-3">Salon-quality nails without leaving home.</h2>
                        <p className="mt-6 text-white/70 leading-relaxed">
                            {city.whyCopy}
                        </p>
                    </div>
                    <div className="space-y-4">
                        {[
                            { icon: Sparkles, t: "Same-day before 6pm", b: "Book by afternoon, we're at your door by evening." },
                            { icon: Clock, t: "Emergency in the hour", b: "Broken nail? Wedding tomorrow? We roll out fast." },
                            { icon: MapPin, t: "We come to you", b: "Home, office, wedding suite — we set up anywhere in {city}." },
                        ].map((item) => {
                            const Icon = item.icon;
                            return (
                                <div key={item.t} className="rounded-2xl border border-white/10 bg-surface p-6 flex gap-4">
                                    <div className="w-11 h-11 rounded-full bg-rose/10 border border-rose/30 flex items-center justify-center shrink-0">
                                        <Icon className="w-5 h-5 text-rose" />
                                    </div>
                                    <div>
                                        <p className="font-serif text-xl">{item.t}</p>
                                        <p className="text-sm text-white/60 mt-1">{item.b.replace("{city}", city.shortName)}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="relative py-24 border-t border-hairline bg-gradient-to-b from-ink to-surface/50">
                <div className="max-w-[900px] mx-auto px-6 md:px-12 text-center">
                    <p className="text-xs tracking-[0.3em] uppercase text-gold">Ready when you are</p>
                    <h2 className="font-serif text-4xl sm:text-5xl mt-3">
                        Your {city.shortName} glow-up is <span className="italic text-rose">one click away.</span>
                    </h2>
                    <p className="mt-6 text-white/70">
                        Join subscribers across {city.name} who&rsquo;ve traded salon trips for door-to-door luxury.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3 justify-center">
                        <Link
                            to="/login?intent=subscribe"
                            data-testid={`city-final-cta-${city.slug}`}
                            className="px-8 py-4 rounded-full bg-rose text-ink font-medium hover:bg-roseHover transition inline-flex items-center gap-2"
                        >
                            Start my subscription <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link
                            to="/skin-quiz"
                            data-testid={`city-quiz-cta-${city.slug}`}
                            className="px-8 py-4 rounded-full border border-white/20 text-white/85 hover:border-rose hover:text-rose transition"
                        >
                            Take the skin quiz
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
            <ConciergeChat />
        </div>
    );
}
